import React from "react";
import {
  declareAiCapability,
  useProvideAiCapability,
} from "../../../ai-omnibar/ai-capability-registry";

/**
 * What the caregiver exclusions list can do for the AI omnibar.
 *
 * `scope: "page"` because these only work while the page is on screen - `app.navigate` declares that
 * it opens that space, so "flagged exclusions this month" comes back as navigate + filter rather than
 * navigate and nothing.
 */
declareAiCapability({
  id: "caregiverExclusions.filter",
  title: "Filter the caregiver exclusions list",
  effect: "prefill",
  description:
    "Sets the filters on Caregivers > Exclusions: the date range, whether the match is flagged, un-flagged or unmatched, and a free-text search. The page opens on the current month. Only works while that page is open.",
  location: "the filter bar of the Caregivers > Exclusions page",
  scope: "page",
  permissionKey: "view_caregivers_section_list",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last day to include, as yyyy-MM-dd.",
    },
    {
      name: "status",
      type: "enum",
      required: false,
      multiple: true,
      description: "Whether the exclusion match is flagged, un-flagged, or not a match.",
      options: [
        { value: "Flagged", label: "Flagged" },
        { value: "Un-Flagged", label: "Un-Flagged" },
        { value: "NoMatch", label: "No Match" },
      ],
    },
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text the list matches against the caregiver's details.",
    },
  ],
  examples: ["flagged exclusions", "exclusions last month", "unmatched exclusions for smith"],
});

export function useCaregiverExclusionsAiCapabilities(params: {
  /** The page's own query params, typed loosely: this hook only sets keys it declares. */
  setValue: (key: string, value: unknown) => void;
}): void {
  const { setValue } = params;

  const setValueRef = React.useRef(setValue);
  setValueRef.current = setValue;

  useProvideAiCapability("caregiverExclusions.filter", (args) => {
    const applied: string[] = [];
    const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);

    if (args.from !== undefined) {
      setValueRef.current("from", String(args.from));
    }

    if (args.to !== undefined) {
      setValueRef.current("to", String(args.to));
    }

    if (args.from !== undefined || args.to !== undefined) {
      applied.push([args.from, args.to].filter(Boolean).join(" to "));
    }

    if (args.status !== undefined) {
      const allowed = ["Flagged", "Un-Flagged", "NoMatch"];
      const statuses = asList(args.status).map((one) => {
        const match = allowed.find(
          (candidate) => candidate.toLowerCase() === String(one).toLowerCase().replace(/\s+/g, "-")
        );

        if (match === undefined) {
          throw new Error(
            `An exclusion is flagged, un-flagged or not a match - not ${String(one)}.`
          );
        }

        return match;
      });

      setValueRef.current("statuses", statuses);
      applied.push(statuses.join(" and "));
    }

    if (args.search !== undefined) {
      setValueRef.current("searchTerm", String(args.search));
      applied.push(`"${String(args.search)}"`);
    }

    return applied.length === 0
      ? "Re-applied the exclusion filters"
      : `Showing exclusions: ${applied.join(", ")}`;
  });
}
